import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";

export default function HowWeWork() {
  const sectionRef = useRef(null);
  const rightRef = useRef(null);
  const isLockedRef = useRef(false);
  const [activeStep, setActiveStep] = useState(0);
  const [hoveredStep, setHoveredStep] = useState(null);

  // Scroll animation for the vertical line
  const { scrollYProgress } = useScroll({
    container: rightRef,
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const lineHeight = useTransform(smoothProgress, [0, 1], ['0%', '100%']);
  const lineGradient = useTransform(smoothProgress, [0, 1], ['#3B82F6', '#8B5CF6']);

  // Lock body scroll when right panel is scrolling - Desktop only
  useEffect(() => {
    const right = rightRef.current;

    if (!right) return;

    const handleWheel = (e) => {
      // Only apply custom handling on desktop-sized viewports
      if (window.innerWidth < 1024) return;

      const atTop = right.scrollTop === 0;
      const atBottom = right.scrollHeight - right.scrollTop <= right.clientHeight + 1;

      // If the right panel can scroll in the direction of the wheel, prevent default and scroll it
      if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atBottom)) {
        e.preventDefault();
        isLockedRef.current = true;
        right.scrollTop += e.deltaY;

        // Clear the lock after a short timeout
        clearTimeout(handleWheel._unlockTimeout);
        handleWheel._unlockTimeout = setTimeout(() => {
          isLockedRef.current = false;
        }, 100);
      } else {
        isLockedRef.current = false;
      }
    };

    const handleTouchMove = (e) => {
      if (isLockedRef.current && window.innerWidth >= 1024) {
        e.preventDefault();
      }
    };

    // Intersection Observer to track active step
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepIndex = parseInt(entry.target.getAttribute('data-step') || '0');
            setActiveStep(stepIndex);
          }
        });
      },
      {
        root: rightRef.current,
        threshold: 0.6,
        rootMargin: '-20% 0px -20% 0px'
      }
    );

    // Observe all step elements
    const stepElements = right?.querySelectorAll('[data-step]');
    stepElements?.forEach((el) => observer.observe(el));

    // Attach listener to the right panel element rather than the window to avoid intercepting early wheel events
    right.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      right.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchmove", handleTouchMove);
      clearTimeout(handleWheel._unlockTimeout);
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative flex flex-col lg:flex-row min-h-screen overflow-hidden bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50"
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

      {/* LEFT SIDE - Mobile: Top, Desktop: Left */}
      <div className="w-full lg:w-2/5 flex flex-col justify-center items-center px-6 lg:px-16 py-16 lg:py-0 lg:sticky lg:top-0 lg:h-screen relative z-10">
        <div className="max-w-lg w-full">
          {/* Header */}
          <div className="text-center lg:text-left mb-12">
            <div className="inline-flex items-center justify-center lg:justify-start mb-4">
              <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
              <p className="text-blue-600 font-semibold text-lg tracking-wider">
                OUR PROCESS
              </p>
              <div className="w-2 h-2 bg-blue-600 rounded-full ml-3"></div>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6 text-gray-900">
              Strategic Steps to
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Impactful Results
              </span>
            </h2>
            
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto lg:mx-0"></div>
          </div>

          {/* Image Card */}
          <motion.div 
            className="relative group"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl opacity-20 group-hover:opacity-30 blur-xl transition-all duration-500"></div>
            <div className="relative bg-white rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl border border-white/50 backdrop-blur-sm">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt="Team collaboration and planning"
                className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Proven Methodology</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our structured approach ensures every project delivers exceptional results through careful planning and execution.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">98%</div>
              <div className="text-sm text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">500+</div>
              <div className="text-sm text-gray-600">Projects</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">24/7</div>
              <div className="text-sm text-gray-600">Support</div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Mobile: Bottom, Desktop: Right with custom scroll */}
      <div
        ref={rightRef}
        className="w-full lg:w-3/5 overflow-y-auto py-16 lg:py-24 px-6 lg:px-16 scroll-smooth hide-scrollbar relative z-10"
        style={{ 
          height: '100vh',
        }}
      >
        <div className="relative">
          {/* Animated Vertical Line with Gradient */}
          <div className="absolute left-8 lg:left-12 top-0 h-full w-1 bg-gradient-to-b from-gray-200 to-gray-200">
            <motion.div
              className="absolute top-0 left-0 w-full origin-top"
              style={{ 
                height: lineHeight,
                background: 'linear-gradient(to bottom, #3B82F6, #8B5CF6)'
              }}
            />
          </div>

          {/* Steps Container */}
          <div className="relative border-l-0 ml-16 lg:ml-20 space-y-16 lg:space-y-20">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                data-step={i}
                className="pl-4 lg:pl-6 relative group"
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.6,
                  ease: "easeOut",
                  delay: i * 0.1
                }}
                viewport={{ 
                  root: rightRef,
                  once: false,
                  margin: "-20% 0px -20% 0px"
                }}
                onMouseEnter={() => setHoveredStep(i)}
                onMouseLeave={() => setHoveredStep(null)}
              >
                {/* Animated Circle */}
                <div className="absolute -left-[52px] lg:-left-[52px] top-2">
                  <motion.div
                    className="w-10 h-10 rounded-full border-2 flex items-center justify-center relative"
                    animate={{
                      borderColor: activeStep >= i ? "#3B82F6" : "#D1D5DB",
                      backgroundColor: activeStep >= i ? "#3B82F6" : "transparent",
                      scale: activeStep === i ? 1.1 : hoveredStep === i ? 1.05 : 1,
                      boxShadow: activeStep === i ? "0 0 0 8px rgba(59, 130, 246, 0.2)" : "none"
                    }}
                    transition={{
                      duration: 0.3,
                      ease: "easeInOut"
                    }}
                  >
                    {/* Step Number */}
                    <motion.span
                      className={`text-sm font-bold ${
                        activeStep >= i ? 'text-white' : 'text-gray-400'
                      }`}
                      animate={{
                        scale: activeStep === i ? 1.2 : 1
                      }}
                    >
                      {i + 1}
                    </motion.span>

                    {/* Pulsing Ring for Active Step */}
                    {activeStep === i && (
                      <motion.div
                        className="absolute inset-0 border-2 border-blue-500 rounded-full"
                        initial={{ scale: 1, opacity: 1 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeOut"
                        }}
                      />
                    )}
                  </motion.div>
                </div>

                {/* Step Content Card */}
                <motion.div
                  className="bg-white/80 backdrop-blur-sm rounded-2xl lg:rounded-3xl p-6 lg:p-8 shadow-lg border border-white/50 hover:shadow-2xl transition-all duration-500"
                  animate={{
                    x: activeStep === i ? 12 : 0,
                    scale: hoveredStep === i ? 1.02 : 1,
                    borderColor: activeStep === i ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeOut"
                  }}
                >
                  {/* Step Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl lg:text-3xl font-bold text-blue-600 mb-2">
                        {step.number}
                      </h3>
                      <h4 className="text-xl lg:text-2xl font-bold text-gray-900">
                        {step.title}
                      </h4>
                    </div>
                    
                    {/* Step Icon */}
                    <motion.div 
                      className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white"
                      whileHover={{ rotate: 5, scale: 1.1 }}
                    >
                      <StepIcon index={i} />
                    </motion.div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed text-base lg:text-lg">
                    {step.description}
                  </p>

                  {/* Progress Indicator */}
                  <div className="mt-6 flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <motion.div 
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: activeStep >= i ? '100%' : '0%' }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      />
                    </div>
                    <span className="text-sm text-gray-500 font-medium">
                      {activeStep > i ? 'Completed' : activeStep === i ? 'In Progress' : 'Pending'}
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
}

// Step Icons Component
const StepIcon = ({ index }) => {
  const icons = [
    <svg key="discovery" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>,
    <svg key="planning" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>,
    <svg key="design" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>,
    <svg key="development" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>,
    <svg key="delivery" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ];

  return icons[index] || icons[0];
};

// Steps Data
const steps = [
  {
    number: "01",
    title: "Discovery & Research",
    description: "We start by deeply understanding your goals, target audience, and market landscape — identifying key insights that shape our bespoke digital strategy through comprehensive analysis and stakeholder interviews.",
  },
  {
    number: "02",
    title: "Strategic Planning",
    description: "We analyze current trends, competitor strategies, and emerging opportunities to craft a clear, actionable roadmap for your digital success with measurable milestones and deliverables.",
  },
  {
    number: "03",
    title: "Creative Design",
    description: "Creative concepts come to life as we design user-friendly, visually appealing, and conversion-optimized digital solutions that reflect your brand identity and resonate with your audience.",
  },
  {
    number: "04",
    title: "Development & Implementation",
    description: "Using cutting-edge technologies and agile methodologies, we develop and implement your project with precision, care, and continuous optimization to ensure robust performance and scalability.",
  },
  {
    number: "05",
    title: "Launch & Growth",
    description: "After a seamless launch, we provide ongoing support, performance monitoring, and strategic adjustments to ensure sustained long-term success, growth, and maximum ROI for your investment.",
  },
];